import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Chip, Alert, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider,
  List, ListItem, ListItemText, ListItemAvatar, Avatar,
} from '@mui/material';
import {
  Add, Gavel, Send, AttachFile, Visibility, Home, Person,
} from '@mui/icons-material';
import { disputesAPI } from '../../api/client';
import useAuthStore from '../../store/useStore';
import { format } from 'date-fns';

const statusColors = {
  open: 'warning', under_review: 'info', mediation: 'secondary',
  resolved: 'success', closed: 'default', escalated: 'error',
};

function DisputesPage() {
  const { user } = useAuthStore();
  const isLandlord = ['landlord', 'agent'].includes(user?.role);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [form, setForm] = useState({
    subject: '', description: '', category: 'other', priority: 'medium',
  });
  const [tenantContext, setTenantContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  const fetchTenantContext = async () => {
    setContextLoading(true);
    try {
      const res = await disputesAPI.getTenantContext();
      if (res.data?.has_context) setTenantContext(res.data);
      else setTenantContext(null);
    } catch (_) { setTenantContext(null); }
    finally { setContextLoading(false); }
  };

  const fetchDisputes = async () => {
    try {
      const res = await disputesAPI.list({ per_page: 50 });
      setDisputes(res.data.disputes || []);
    } catch (err) {
      setError('Failed to load disputes');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchDisputes(); }, []);

  const handleCreate = async () => {
    try {
      await disputesAPI.create(form);
      setSuccess('Dispute filed successfully');
      setCreateOpen(false);
      setForm({ subject: '', description: '', category: 'other', priority: 'medium' });
      fetchDisputes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create dispute');
    }
  };

  const handleView = async (dispute) => {
    setSelectedDispute(dispute);
    setViewOpen(true);
    try {
      const res = await disputesAPI.getMessages(dispute.id);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (_) { setMessages([]); }
  };

  const handleSendMsg = async () => {
    if (!newMsg.trim() || !selectedDispute) return;
    try {
      await disputesAPI.addMessage(selectedDispute.id, { message: newMsg.trim() });
      setNewMsg('');
      const res = await disputesAPI.getMessages(selectedDispute.id);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (_) { setError('Failed to send message'); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Disputes</Typography>
          <Typography variant="body2" color="text.secondary">
            {isLandlord ? 'View and manage disputes from your tenants' : 'File and track your disputes'}
          </Typography>
        </Box>
        {!isLandlord && (
          <Button variant="contained" startIcon={<Add />} onClick={() => { setCreateOpen(true); fetchTenantContext(); }}>
            File Dispute
          </Button>
        )}
      </Stack>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {disputes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Gavel sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No disputes found</Typography>
          {isLandlord ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>Disputes filed by your tenants will appear here for you to review and manage.</Typography>
          ) : (
            <>
              <Typography color="text.secondary" sx={{ mb: 2 }}>File a dispute if you have an issue with a landlord, property, or booking.</Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={() => { setCreateOpen(true); fetchTenantContext(); }}>File a Dispute</Button>
            </>
          )}
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Dispute #</strong></TableCell>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {disputes.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{d.dispute_number}</Typography></TableCell>
                  <TableCell>{d.subject}</TableCell>
                  <TableCell><Chip label={d.category?.replace('_', ' ')} size="small" sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell><Chip label={d.status?.replace('_', ' ')} size="small" color={statusColors[d.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell><Chip label={d.priority} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell>{d.created_at ? format(new Date(d.created_at), 'dd/MM/yyyy') : '-'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(d)}><Visibility /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>File a Dispute</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {tenantContext && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Home fontSize="small" /> Auto-filled Tenant Details
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Hostel / Property</Typography>
                    <Typography variant="body2" fontWeight={600}>{tenantContext.property?.title || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Room Number</Typography>
                    <Typography variant="body2" fontWeight={600}>{tenantContext.booking?.room_number || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Location</Typography>
                    <Typography variant="body2">{tenantContext.property?.address}, {tenantContext.property?.city}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Booking Status</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{tenantContext.booking?.status}</Typography>
                  </Grid>
                  {tenantContext.landlord && (
                    <>
                      <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Landlord / Agent</Typography>
                        <Typography variant="body2" fontWeight={600}>{tenantContext.landlord.name}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Contact</Typography>
                        <Typography variant="body2">{tenantContext.landlord.email}</Typography>
                        {tenantContext.landlord.phone && <Typography variant="body2">{tenantContext.landlord.phone}</Typography>}
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            )}
            {contextLoading && <Typography variant="body2" color="text.secondary">Loading your booking details...</Typography>}
            {!contextLoading && !tenantContext && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>No active booking found. Tenant details will not be auto-filled.</Alert>
            )}
            <TextField fullWidth label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            <TextField fullWidth select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} SelectProps={{ native: true }}>
              <option value="rent_dispute">Rent Dispute</option>
              <option value="deposit_return">Deposit Return</option>
              <option value="maintenance">Maintenance</option>
              <option value="eviction">Eviction</option>
              <option value="contract_breach">Contract Breach</option>
              <option value="noise_complaint">Noise Complaint</option>
              <option value="property_damage">Property Damage</option>
              <option value="utility_charges">Utility Charges</option>
              <option value="other">Other</option>
            </TextField>
            <TextField fullWidth select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} SelectProps={{ native: true }}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </TextField>
            <TextField fullWidth label="Description" multiline rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.subject || !form.description}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Dispute {selectedDispute?.dispute_number}</Typography>
            <Chip label={selectedDispute?.status?.replace('_', ' ')} color={statusColors[selectedDispute?.status] || 'default'} size="small" sx={{ textTransform: 'capitalize' }} />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight={600}>{selectedDispute?.subject}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{selectedDispute?.description}</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Category</Typography><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedDispute?.category?.replace('_', ' ')}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Priority</Typography><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedDispute?.priority}</Typography></Grid>
          </Grid>

          {selectedDispute?.resolution && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Resolution</Typography>
              <Typography variant="body2">{selectedDispute.resolution}</Typography>
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Messages</Typography>
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {messages.length === 0 ? (
              <ListItem><ListItemText secondary="No messages yet" /></ListItem>
            ) : messages.map((m) => (
              <ListItem key={m.id} alignItems="flex-start">
                <ListItemAvatar><Avatar sx={{ width: 32, height: 32 }}>{m.sender_id === user?.id ? 'Y' : 'A'}</Avatar></ListItemAvatar>
                <ListItemText
                  primary={m.message}
                  secondary={m.created_at ? format(new Date(m.created_at), 'dd/MM/yy HH:mm') : ''}
                />
              </ListItem>
            ))}
          </List>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField fullWidth size="small" placeholder="Type a message..." value={newMsg} onChange={(e) => setNewMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSendMsg()} />
            <IconButton color="primary" onClick={handleSendMsg}><Send /></IconButton>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DisputesPage;
